/**
 * ReviewWorkspace — every proposed change, one at a time, at a size you can
 * read: what it fixes, where it came from, and exactly what it changes, as
 * marked-up changes, before and after side by side, or the new text alone.
 *
 * Pure view of a ``ReviewState`` — it renders in the review window (an Office
 * dialog) and, where dialogs are unavailable, in a full-pane overlay. Every
 * click goes out through a callback; the task pane applies it.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import remarkGfm from "remark-gfm";
import { htmlTablesAsText } from "../../services/formatter/htmlTable";
import { MarkdownWithTables } from "../components/MarkdownWithTables";
import { TableDiffView } from "../components/TableDiffView";
import { diffSummary, type DiffSegment, wordDiff } from "../services/wordDiff";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import type { ReviewItem, ReviewState } from "./reviewProtocol";
import { SizeToggle, type ViewPlacement } from "./SizeToggle";

const ADDED_BG = "#E8F6EC";
const ADDED_FG = "#1E6B34";
const REMOVED_BG = "#FCEBEC";
const REMOVED_FG = "#A3202E";
const SERIF = "'Source Serif 4', Georgia, 'Times New Roman', serif";

export type ReviewView = "changes" | "side" | "new";

export interface ReviewWorkspaceProps {
  state: ReviewState;
  onSelect: (id: string) => void;
  /** ``content``: the writer's edit of the proposed text, when they edited it. */
  onApprove: (id: string, content?: string) => void;
  onReject: (id: string) => void;
  onRevise: (id: string, guidance: string) => void;
  onApproveAll: () => void;
  onStopAll: () => void;
  /** Undo the change just approved (shown while ``state.undoSection`` is set). */
  onUndo?: () => void;
  onClose: () => void;
  /** Where this view is showing, and the Expand / Minimise switch (absent when
   *  this Word cannot open a window). */
  placement?: ViewPlacement;
  onResize?: () => void;
}

const useStyles = makeStyles({
  root: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: surfaceColors.paper,
    color: inkColors.ink,
    fontFamily: fonts.body,
    fontSize: "13px",
  },
  top: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  title: { fontSize: "15px", fontWeight: 600 },
  meta: { fontSize: "12px", color: inkColors.ink3 },
  undoBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink2,
    backgroundColor: surfaceColors.paperDim,
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  linkBtn: {
    border: "none",
    background: "none",
    padding: 0,
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 600,
    color: inkColors.ink,
    textDecoration: "underline",
    cursor: "pointer",
  },
  spacer: { flex: 1 },
  main: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    "@media (max-width: 640px)": { flexDirection: "column" },
  },
  list: {
    width: "280px",
    flexShrink: 0,
    overflowY: "auto",
    borderRight: `1px solid ${surfaceColors.line}`,
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    "@media (max-width: 640px)": {
      width: "auto",
      maxHeight: "132px",
      borderRight: "none",
      borderBottom: `1px solid ${surfaceColors.line}`,
    },
  },
  row: {
    textAlign: "left",
    border: "1px solid transparent",
    borderRadius: "8px",
    background: "transparent",
    padding: "8px 10px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    fontFamily: fonts.body,
    color: inkColors.ink2,
    ":hover": { backgroundColor: surfaceColors.paperDim },
  },
  rowActive: {
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
  rowHead: { fontSize: "13px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowSub: {
    fontSize: "12px",
    color: inkColors.ink3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  detail: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  detailScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  detailHead: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
  number: { fontFamily: fonts.mono, fontSize: "13px", fontWeight: 600, color: inkColors.ink2 },
  sectionTitle: { fontSize: "15px", fontWeight: 600 },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "999px",
    padding: "1px 10px",
  },
  dot: { width: "7px", height: "7px", borderRadius: "50%", backgroundColor: statusColors.warn },
  dotBusy: { backgroundColor: brandColors.primary },
  facts: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: "12px",
    rowGap: "4px",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  factLabel: {
    color: inkColors.ink4,
    textTransform: "uppercase",
    fontSize: "11px",
    letterSpacing: "0.04em",
    paddingTop: "1px",
  },
  factList: { margin: 0, paddingLeft: "16px", color: inkColors.ink2 },
  stillOpen: { color: statusColors.danger },
  seg: {
    display: "inline-flex",
    alignSelf: "flex-start",
    padding: "2px",
    borderRadius: "8px",
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
  },
  segBtn: {
    height: "28px",
    padding: "0 12px",
    border: 0,
    borderRadius: "6px",
    background: "none",
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink3,
    cursor: "pointer",
  },
  segBtnOn: {
    backgroundColor: surfaceColors.paper,
    color: inkColors.ink,
    fontWeight: 600,
    boxShadow: `0 0 0 1px ${surfaceColors.lineStrong}`,
  },
  doc: {
    fontFamily: SERIF,
    fontSize: "14px",
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    padding: "12px 14px",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
  },
  docMd: {
    whiteSpace: "normal",
    "& table": { borderCollapse: "collapse", fontFamily: fonts.body, fontSize: "12px", margin: "8px 0" },
    "& th, & td": { border: `1px solid ${surfaceColors.lineStrong}`, padding: "4px 8px", verticalAlign: "top" },
    "& p": { margin: "0 0 10px" },
  },
  sideBySide: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    "@media (max-width: 640px)": { gridTemplateColumns: "1fr" },
  },
  colLabel: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: inkColors.ink4,
    marginBottom: "4px",
  },
  ins: {
    backgroundColor: ADDED_BG,
    color: ADDED_FG,
    textDecorationLine: "underline",
    textUnderlineOffset: "2px",
    borderRadius: "2px",
  },
  del: { backgroundColor: REMOVED_BG, color: REMOVED_FG, textDecorationLine: "line-through", borderRadius: "2px" },
  summary: { fontSize: "12px", color: inkColors.ink3 },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  error: {
    fontSize: "12px",
    color: REMOVED_FG,
    border: `1px solid ${REMOVED_BG}`,
    backgroundColor: "#FFF8F8",
    borderRadius: "8px",
    padding: "8px 10px",
  },
  footerError: { margin: "8px 20px 0" },
  progress: { fontSize: "12px", color: inkColors.ink3, fontStyle: "italic" },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "64px",
    resize: "vertical",
    fontFamily: fonts.body,
    fontSize: "13px",
    padding: "8px 10px",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
  editArea: { minHeight: "220px", fontFamily: SERIF, fontSize: "14px", lineHeight: 1.5 },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    borderTop: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paperDim,
    flexWrap: "wrap",
  },
  note: { fontSize: "12px", color: inkColors.ink3 },
  btn: {
    height: "32px",
    padding: "0 14px",
    borderRadius: "6px",
    fontFamily: fonts.body,
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    border: `1px solid ${surfaceColors.lineStrong}`,
    backgroundColor: surfaceColors.paper,
    color: inkColors.ink2,
    ":disabled": { opacity: 0.5, cursor: "default" },
  },
  btnPrimary: {
    border: `1px solid ${brandColors.primary}`,
    backgroundColor: brandColors.primary,
    color: "#FFFFFF",
  },
  btnGhost: { border: "1px solid transparent", backgroundColor: "transparent" },
  empty: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: inkColors.ink3,
  },
});

function words(n: number): string {
  return `${n} word${n === 1 ? "" : "s"}`;
}

/** Status in words, for the pill beside the section heading. */
export function statusLabel(state: ReviewState, item: ReviewItem): string {
  if (item.id !== state.activeId) return "Awaiting you";
  if (state.phase === "applying") return "Applying";
  if (state.phase === "revising") return "Rewriting";
  return "Awaiting you";
}

const Segments: React.FC<{ segments: readonly DiffSegment[] }> = ({ segments }) => {
  const styles = useStyles();
  return (
    <>
      {segments.map((s, i) => {
        // Diff segments are positional and never reorder.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
        if (s.kind === "same") return <span key={i}>{s.text}</span>;
        if (s.kind === "added") {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
            <ins key={i} className={styles.ins}>
              {s.text}
            </ins>
          );
        }
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
          <del key={i} className={styles.del}>
            {s.text}
          </del>
        );
      })}
    </>
  );
};

/** A figure pointer, as a reader sees it: "[Figure: <caption>]". */
export function showFigures(text: string): string {
  return text.replace(/\[IMAGE DATA:\s*([^\]]+)\]/g, "[Figure: $1]");
}

export const ChangeBody: React.FC<{ item: ReviewItem; view: ReviewView }> = ({ item, view }) => {
  const styles = useStyles();
  const before = showFigures(item.before);
  const after = showFigures(item.after);
  const segments = useMemo(() => wordDiff(htmlTablesAsText(before), htmlTablesAsText(after)), [before, after]);
  const { added, removed } = diffSummary(segments);

  if (item.kind === "field") {
    return (
      <div className={styles.field}>
        <div className={styles.colLabel}>{item.fieldLabel}</div>
        <div className={styles.doc}>
          <del className={styles.del}>{item.before || "(empty)"}</del>
          {"  "}
          <ins className={styles.ins}>{item.after}</ins>
        </div>
      </div>
    );
  }
  if (item.kind === "table" && item.table) {
    return <TableDiffView snippet={item.table} />;
  }
  if (view === "new") {
    return (
      <div className={mergeClasses(styles.doc, styles.docMd)}>
        <MarkdownWithTables remarkPlugins={[remarkGfm]}>{after}</MarkdownWithTables>
      </div>
    );
  }
  // Before and after: the two texts as they read, no marks. "Show changes"
  // is where the differences are marked.
  if (view === "side") {
    return (
      <div className={styles.sideBySide}>
        <div>
          <div className={styles.colLabel}>Before</div>
          <div className={mergeClasses(styles.doc, styles.docMd)}>
            <MarkdownWithTables remarkPlugins={[remarkGfm]}>{before}</MarkdownWithTables>
          </div>
        </div>
        <div>
          <div className={styles.colLabel}>After</div>
          <div className={mergeClasses(styles.doc, styles.docMd)}>
            <MarkdownWithTables remarkPlugins={[remarkGfm]}>{after}</MarkdownWithTables>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className={styles.doc}>
        <Segments segments={segments} />
      </div>
      <div className={styles.summary}>
        Struck = removed ({words(removed)}) · Underlined = added ({words(added)})
      </div>
    </>
  );
};

export const ReviewWorkspace: React.FC<ReviewWorkspaceProps> = ({
  state,
  onSelect,
  onApprove,
  onReject,
  onRevise,
  onApproveAll,
  onStopAll,
  onUndo,
  onClose,
  placement = "pane",
  onResize,
}) => {
  const styles = useStyles();
  const [view, setView] = useState<ReviewView>("changes");
  const [reviseOpen, setReviseOpen] = useState(false);
  const [guidance, setGuidance] = useState("");
  // "Edit text": the writer's own wording, approved in place of the proposal.
  const [editText, setEditText] = useState<string | null>(null);
  const active = state.items.find((i) => i.id === state.activeId) ?? state.items[0] ?? null;
  const busy = state.phase === "applying" || state.phase === "revising" || state.approvingAll;
  const count = state.items.length;

  // A new change under review starts with a closed Revise box.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the active change only
  useEffect(() => {
    setReviseOpen(false);
    setGuidance("");
    setEditText(null);
  }, [active?.id]);

  // "Change applied · Undo" — after an approval, for a few seconds.
  const undoBar =
    state.undoSection && onUndo ? (
      <div className={styles.undoBar} role="status">
        <span>Change applied in §{state.undoSection}.</span>
        <button type="button" className={styles.linkBtn} onClick={onUndo}>
          Undo
        </button>
      </div>
    ) : null;

  if (!active) {
    return (
      <div className={styles.root}>
        {undoBar}
        <div className={styles.empty}>
          <div className={styles.title}>{state.phase === "loading" ? "Loading changes" : "Nothing left to review"}</div>
          {state.error ? <div className={styles.error}>{state.error}</div> : null}
          <button type="button" className={styles.btn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const sendRevise = () => {
    const g = guidance.trim();
    if (!g) return;
    onRevise(active.id, g);
    setReviseOpen(false);
    setGuidance("");
  };

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <span className={styles.title}>Review changes</span>
        <span className={styles.meta}>
          {count} waiting{state.newCount ? ` · ${state.newCount} new` : ""}
        </span>
        <span className={styles.spacer} />
        <SizeToggle placement={placement} onToggle={onResize} />
        <button type="button" className={mergeClasses(styles.btn, styles.btnGhost)} onClick={onClose}>
          Close
        </button>
      </div>
      {undoBar}

      <div className={styles.main}>
        <nav className={styles.list} aria-label="Changes">
          {state.items.map((item) => (
            <button
              type="button"
              key={item.id}
              className={mergeClasses(styles.row, item.id === active.id && styles.rowActive)}
              aria-current={item.id === active.id ? "true" : undefined}
              onClick={() => onSelect(item.id)}
              disabled={busy && item.id !== active.id}
            >
              <span className={styles.rowHead}>
                {item.kind === "field"
                  ? `Title page · ${item.fieldLabel}`
                  : `${item.sectionNumber} ${item.sectionTitle}`}
              </span>
              <span className={styles.rowSub}>
                {item.sourceNote ?? (item.fills.length > 0 ? `Fixes: ${item.fills[0]}` : "Rewrite")}
              </span>
            </button>
          ))}
        </nav>

        <section className={styles.detail} aria-label="Change under review">
          <div className={styles.detailScroll}>
            <div className={styles.detailHead}>
              <span className={styles.number}>{active.kind === "field" ? "Title page" : active.sectionNumber}</span>
              <span className={styles.sectionTitle}>
                {active.kind === "field" ? active.fieldLabel : active.sectionTitle}
              </span>
              <span className={styles.pill}>
                <span className={mergeClasses(styles.dot, busy && styles.dotBusy)} aria-hidden />
                {statusLabel(state, active)}
              </span>
            </div>

            <div className={styles.facts}>
              {active.sourceNote ? (
                <>
                  <span className={styles.factLabel}>Source</span>
                  <span>{active.sourceNote}</span>
                </>
              ) : null}
              {active.fills.length > 0 ? (
                <>
                  <span className={styles.factLabel}>Fixes</span>
                  <ul className={styles.factList}>
                    {active.fills.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              {active.stillOpen.length > 0 ? (
                <>
                  <span className={styles.factLabel}>Still open</span>
                  <ul className={mergeClasses(styles.factList, styles.stillOpen)}>
                    {active.stillOpen.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>

            {active.kind === "text" ? (
              // biome-ignore lint/a11y/useSemanticElements: a segmented control, not a form fieldset
              <div className={styles.seg} role="group" aria-label="View">
                {(
                  [
                    ["changes", "Show changes"],
                    ["side", "Before and after"],
                    ["new", "New text"],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    className={mergeClasses(styles.segBtn, view === key && styles.segBtnOn)}
                    aria-pressed={view === key}
                    onClick={() => setView(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}

            {editText !== null ? (
              <textarea
                className={mergeClasses(styles.textarea, styles.editArea)}
                aria-label="Edit the new text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                // biome-ignore lint/a11y/noAutofocus: focus the box the writer just opened
                autoFocus
              />
            ) : (
              <ChangeBody item={active} view={view} />
            )}

            {state.phase === "revising" && state.progressLabel ? (
              <div className={styles.progress}>{state.progressLabel}</div>
            ) : null}

            {reviseOpen ? (
              <div>
                <textarea
                  className={styles.textarea}
                  placeholder="Say how this change should be different"
                  value={guidance}
                  onChange={(e) => setGuidance(e.target.value)}
                  // biome-ignore lint/a11y/noAutofocus: focus the box the writer just opened
                  autoFocus
                />
              </div>
            ) : null}
          </div>

          {/* Next to the buttons, not under the change: below a long diff a failed
              Approve looked like nothing happened. */}
          {state.error ? (
            <div className={mergeClasses(styles.error, styles.footerError)} role="alert">
              {state.error}
            </div>
          ) : null}
          <div className={styles.footer}>
            {state.approvingAll ? (
              <button type="button" className={styles.btn} onClick={onStopAll}>
                Stop approving
              </button>
            ) : count > 1 ? (
              <button type="button" className={styles.btn} onClick={onApproveAll} disabled={busy}>
                Approve all {count}
              </button>
            ) : null}
            <span className={styles.note}>Nothing in Word changes until you approve.</span>
            <span className={styles.spacer} />
            {editText !== null ? (
              <>
                <button
                  type="button"
                  className={mergeClasses(styles.btn, styles.btnGhost)}
                  onClick={() => setEditText(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={mergeClasses(styles.btn, styles.btnPrimary)}
                  onClick={() => onApprove(active.id, editText === active.after ? undefined : editText)}
                  disabled={busy || !editText.trim()}
                >
                  {state.phase === "applying" ? "Applying" : "Approve edited text"}
                </button>
              </>
            ) : reviseOpen ? (
              <>
                <button
                  type="button"
                  className={mergeClasses(styles.btn, styles.btnGhost)}
                  onClick={() => setReviseOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={mergeClasses(styles.btn, styles.btnPrimary)}
                  onClick={sendRevise}
                  disabled={busy || !guidance.trim()}
                >
                  Rewrite
                </button>
              </>
            ) : (
              <>
                {active.kind === "text" && state.canRevise !== false ? (
                  <button
                    type="button"
                    className={mergeClasses(styles.btn, styles.btnGhost)}
                    onClick={() => setReviseOpen(true)}
                    disabled={busy}
                  >
                    Revise
                  </button>
                ) : null}
                {active.kind === "text" && state.canEdit ? (
                  <button
                    type="button"
                    className={mergeClasses(styles.btn, styles.btnGhost)}
                    onClick={() => setEditText(active.after)}
                    disabled={busy}
                  >
                    Edit text
                  </button>
                ) : null}
                <button type="button" className={styles.btn} onClick={() => onReject(active.id)} disabled={busy}>
                  Reject
                </button>
                <button
                  type="button"
                  className={mergeClasses(styles.btn, styles.btnPrimary)}
                  onClick={() => onApprove(active.id)}
                  disabled={busy}
                >
                  {state.phase === "applying" ? "Applying" : "Approve"}
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReviewWorkspace;
